import { computed, makeObservable, observable, runInAction } from 'mobx';

/** The fields the store reads from a contact mapping. */
export interface IIntegrationContactMappingBase {
  id: string;
  required: boolean;
}

/** The fields the store reads from a mapping update. */
export interface IIntegrationContactMappingUpdateBase {
  mapping: string;
}

/** The integration endpoints the store wraps. */
export interface IIntegrationContactMappingSource<
  TMapping extends IIntegrationContactMappingBase,
  TKey extends string,
  TUpdate extends IIntegrationContactMappingUpdateBase,
> {
  getContactMappings(): Promise<TMapping[]>;
  addContactMappings(keys: TKey[]): Promise<TMapping[]>;
  /** Resolves `null` when the server deleted the mapping instead of updating it. */
  updateContactMapping(change: TUpdate): Promise<TMapping | null>;
  deleteContactMapping(id: string): Promise<void>;
}

function isRejected(result: PromiseSettledResult<unknown>): result is PromiseRejectedResult {
  return result.status === 'rejected';
}

/**
 * Observable contact mappings for one CRM integration. Wraps the integration's mapping
 * endpoints so add, update and remove update the list in place instead of needing a page reload.
 *
 * Every change reloads the list from the server before it settles, whether the request succeeded
 * or failed. The server saves a change before it checks the webhook subscriptions, so a request
 * that failed can still have changed the mappings, and a change can alter
 * `has_provisioning_subscriptions` on other rows.
 */
export class IntegrationContactMappingStore<
  TMapping extends IIntegrationContactMappingBase,
  TKey extends string,
  TUpdate extends IIntegrationContactMappingUpdateBase,
> {
  /**
   * The integration's mappings, or `undefined` until the first load succeeds. The array is always
   * replaced whole, never mutated, so a shallow reference is enough to notify observers.
   */
  @observable.ref public mappings: TMapping[] | undefined = undefined;

  @observable public isPending = false;

  /** The last failed load that no newer load has superseded. Cleared by the next successful load. */
  @observable public error: Error | null = null;

  private readonly integration: IIntegrationContactMappingSource<TMapping, TKey, TUpdate>;

  /** Counts every load and change, so each load knows how recent it is. */
  private generation = 0;

  /**
   * The generation `mappings` currently reflects. A load older than it is discarded, so a reload
   * can't overwrite fresher data.
   */
  private appliedGeneration = 0;

  private pendingLoads = 0;

  private fetchPromise: Promise<void> | null = null;

  public constructor(integration: IIntegrationContactMappingSource<TMapping, TKey, TUpdate>) {
    this.integration = integration;
    makeObservable(this);
  }

  @computed
  public get isFetched(): boolean {
    return this.mappings !== undefined;
  }

  /** Mappings with required ones first, otherwise in server order. */
  @computed
  public get sortedMappings(): TMapping[] {
    const mappings = this.mappings ?? [];
    return [
      ...mappings.filter(({ required }) => required),
      ...mappings.filter(({ required }) => !required),
    ];
  }

  /** Loads the mappings once. Later calls reuse the first load or its result. */
  public fetch(): Promise<void> {
    if (this.isFetched) {
      return Promise.resolve();
    }
    this.fetchPromise ??= this.load().finally(() => {
      this.fetchPromise = null;
    });
    return this.fetchPromise;
  }

  /** Reloads the mappings from the server. Resolves once the reload has settled; it never rejects. */
  public refresh(): Promise<void> {
    return this.load();
  }

  /**
   * Adds a mapping for each key, one request per key. The mappings the server created are kept
   * even when another key fails; the first failure is rethrown after the list has reloaded.
   */
  public async add(keys: TKey[]): Promise<TMapping[]> {
    const results = await Promise.allSettled(
      keys.map((key) => this.integration.addContactMappings([key])),
    );
    const added = results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
    const addedIds = new Set(added.map(({ id }) => id));

    this.applyChange((mappings) => [...mappings.filter(({ id }) => !addedIds.has(id)), ...added]);
    await this.refresh();

    const failure = results.find(isRejected);
    if (failure) {
      throw failure.reason;
    }
    return added;
  }

  /**
   * Resolves `null` when the server deleted the mapping instead: a magpie-owned integration has no
   * mapping without a Feathr field.
   */
  public update(change: TUpdate): Promise<TMapping | null> {
    return this.change(this.integration.updateContactMapping(change), (mappings, updated) =>
      updated
        ? mappings.map((mapping) => (mapping.id === updated.id ? updated : mapping))
        : mappings.filter(({ id }) => id !== change.mapping),
    );
  }

  public remove(id: string): Promise<void> {
    return this.change(this.integration.deleteContactMapping(id), (mappings) =>
      mappings.filter((mapping) => mapping.id !== id),
    );
  }

  /**
   * Awaits a change request, applies its result to the list, then reloads before settling. A
   * failed request reloads too, then rethrows.
   */
  private async change<T>(
    request: Promise<T>,
    apply: (mappings: TMapping[], result: T) => TMapping[],
  ): Promise<T> {
    let result: T;
    try {
      result = await request;
    } catch (error) {
      await this.refresh();
      throw error;
    }
    this.applyChange((mappings) => apply(mappings, result));
    await this.refresh();
    return result;
  }

  private async load(): Promise<void> {
    const generation = ++this.generation;
    this.pendingLoads += 1;
    runInAction(() => {
      this.isPending = true;
    });

    try {
      const mappings = await this.integration.getContactMappings();
      runInAction(() => {
        if (generation > this.appliedGeneration) {
          this.appliedGeneration = generation;
          this.mappings = mappings;
          this.error = null;
        }
      });
    } catch (error) {
      runInAction(() => {
        if (generation > this.appliedGeneration) {
          this.error = error instanceof Error ? error : new Error(String(error));
        }
      });
    } finally {
      this.pendingLoads -= 1;
      runInAction(() => {
        this.isPending = this.pendingLoads > 0;
      });
    }
  }

  /**
   * Applies a successful change to the list and marks every load started before it as stale.
   * Before the first load there is no list to change; the reload that follows every change
   * loads it.
   */
  private applyChange(change: (mappings: TMapping[]) => TMapping[]): void {
    this.appliedGeneration = ++this.generation;
    const { mappings } = this;
    if (!mappings) {
      return;
    }
    runInAction(() => {
      this.mappings = change(mappings);
    });
  }
}
